import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Grid, Link, Typography } from '@mui/material';
import { InsuranceClaim, InsuranceClaimStatus } from './types';
import { INSURANCE_CLAIM_STATUS_OPTIONS } from './fields';
import { formatCurrency } from '../../../utils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HOSPITAL_BILLING_INSURANCE_VIEW, HOSPITAL_BILLING_VIEW_DOCUMENT } from '../../../routes/urls';

interface InsuranceClaimViewProps {
    data: InsuranceClaim;
    translate: (key: string) => string;
    currency: string;
    locale: string;
}

/**
 * Insurance Claim View Component
 * 
 * Displays insurance claim data in a read-only format for approved/paid claims.
 * Used when claim status is APPROVED or PAID and no editing is allowed.
 */
const InsuranceClaimView: React.FC<InsuranceClaimViewProps> = ({
    data,
    translate,
    currency,
    locale
}) => {
    /**
     * Get status color from INSURANCE_CLAIM_STATUS_OPTIONS
     */
    const statusColor = useMemo(() => {
        const statusOption = INSURANCE_CLAIM_STATUS_OPTIONS.find(opt => opt.value === data.status);
        return (statusOption?.color as "default" | "info" | "success" | "warning" | "error" | "primary") || "default";
    }, [data.status]);

    /**
     * Format date with locale
     */
    const formatDate = useCallback((date: Date | string | undefined): string => {
        if (!date) return '-';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) return '-';
        return dateObj.toLocaleDateString(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }, [locale]);

    /**
     * Format percentage
     */
    const formatPercentage = useCallback((value: number | undefined | null): string => {
        if (value === undefined || value === null) return '-';
        return `${value}%`;
    }, []);

    /**
     * Render field component
     */
    const renderField = (
        label: string,
        value: string | number | React.ReactNode,
        xs: number = 12,
        lg: number = 6
    ) => (
        <Grid item xs={xs} lg={lg}>
            <Box mb={2}>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {translate(label)}
                </Typography>
                <Typography variant="body1" color="textPrimary">
                    {value || '-'}
                </Typography>
            </Box>
        </Grid>
    );

    return (
        <Box data-testid="insurance-claim-view" p={2}>
            <Grid container spacing={2}>
                {/* Organization Name */}
                {renderField(
                    'hospital.billing.insurance_claims.form.organization_name',
                    data.organizationName
                )}

                {/* Souscripteur — omitted entirely when absent, so an empty value
                    never renders as a blank label (SHER-713) */}
                {data.souscripteur && renderField(
                    'hospital.billing.insurance_claims.form.souscripteur',
                    data.souscripteur
                )}

                {/* Status */}
                <Grid item xs={12} lg={6}>
                    <Box mb={2}>
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            {translate('hospital.billing.insurance_claims.form.status')}
                        </Typography>
                        <Chip 
                            label={translate(`hospital.billing.insurance_claims.form.status_${data.status.toLowerCase()}`)}
                            color={statusColor}
                            size="small"
                        />
                    </Box>
                </Grid>

                {/* Coverage Percentage */}
                {(data.coveragePercentage !== undefined && data.coveragePercentage !== null) && renderField(
                    'hospital.billing.insurance_claims.form.coverage_percentage',
                    formatPercentage(data.coveragePercentage)
                )}

                {/* Requested Amount */}
                {(data.requestedAmount !== undefined && data.requestedAmount !== null) && renderField(
                    'hospital.billing.insurance_claims.form.requested_amount',
                    formatCurrency(data.requestedAmount, currency, locale)
                )}

                {/* Approved Amount - Only show if status is APPROVED or PAID */}
                {(data.status === InsuranceClaimStatus.APPROVED || data.status === InsuranceClaimStatus.PAID) && 
                 (data.approvedAmount !== undefined && data.approvedAmount !== null) && renderField(
                    'hospital.billing.insurance_claims.form.approved_amount',
                    formatCurrency(data.approvedAmount, currency, locale)
                )}

                {/* Insurance Bill Link - Only show if status is APPROVED or PAID and insuranceBillUuid exists */}
                {(data.status === InsuranceClaimStatus.APPROVED || data.status === InsuranceClaimStatus.PAID) && 
                 data.insuranceBillUuid && (
                    <Grid item xs={12} lg={6}>
                        <Box mb={2}>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                {translate('hospital.billing.insurance_claims.form.insurance_bill')}
                            </Typography>
                            <Link
                                href={`${HOSPITAL_BILLING_INSURANCE_VIEW.replace(':uuidBill', data.insuranceBillUuid)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {translate('hospital.billing.insurance_claims.form.view_insurance_bill')}
                                <OpenInNewIcon fontSize="small" />
                            </Link>
                        </Box>
                    </Grid>
                )}

                {/* Guarantee Letter Number */}
                {data.guaranteeLetterNumber && renderField(
                    'hospital.billing.insurance_claims.form.guarantee_letter_number',
                    data.guaranteeLetterNumber,
                    12,
                    12
                )}

                {/* Guarantee Letter Issue Date */}
                {data.guaranteeLetterIssueDate && renderField(
                    'hospital.billing.insurance_claims.form.guarantee_letter_issue_date',
                    formatDate(data.guaranteeLetterIssueDate)
                )}

                {/* Intervention Date */}
                {data.interventionDate && renderField(
                    'hospital.billing.insurance_claims.form.intervention_date',
                    formatDate(data.interventionDate)
                )}

                {/* Amount in Letters */}
                {data.amountInLetters && renderField(
                    'hospital.billing.insurance_claims.form.amount_in_letters',
                    data.amountInLetters,
                    12,
                    12
                )}

                {/* Guarantee Letter PDF */}
                {data.guaranteeLetterPdfPath && (
                    <Grid item xs={12} lg={6}>
                        <Box mb={2}>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                {translate('hospital.billing.insurance_claims.form.guarantee_letter_pdf')}
                            </Typography>
                            <Box mt={1}>
                                <img 
                                    src={data.guaranteeLetterPdfPath} 
                                    alt="Guarantee Letter" 
                                    style={{ 
                                        maxWidth: '200px', 
                                        maxHeight: '200px', 
                                        cursor: 'pointer',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '4px'
                                    }}
                                    onClick={() => {
                                        if (data.guaranteeLetterPdfPath) {
                                            window.open(data.guaranteeLetterPdfPath, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            if (data.guaranteeLetterPdfPath) {
                                                window.open(data.guaranteeLetterPdfPath, '_blank', 'noopener,noreferrer');
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                )}

                {/* Notes */}
                {data.notes && (
                    <Grid item xs={12}>
                        <Box mb={2}>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                {translate('hospital.billing.insurance_claims.form.notes')}
                            </Typography>
                            <Typography variant="body1" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
                                {data.notes}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {/* Claim Reference - if available */}
                {data.claimReference && renderField(
                    'hospital.billing.insurance_claims.form.claim_reference',
                    data.claimReference,
                    12,
                    6
                )}
            </Grid>
        </Box>
    );
};

export default InsuranceClaimView;
